import React from 'react';

import { Inject } from '@odinjs/odin';

import { catchRenderException } from '@philips-emr/hpms-components';
import { ChecklistAPI, ChecklistItem, FieldAPI, FieldsetAPI, FormAPI, InputAPI, LinkAPI, PanelAPI, PhoneNumberAPI, WizardAPI } from '@philips-emr/tws-components';
import { Country, ThemeTonalRange } from '@philips-emr/tws-constants';
import Controller, { OnAfterFetch, OnAfterNavigate, OnBeforeSubmit, OnChange, OnMount, OnNavigate, OnValidate } from '@philips-emr/tws-core/lib/client';
import { AbortableNavigateEvent, AbortableSubmitEvent, ChangeEvent, FetchEvent, MountEvent, NavigateEvent, ValidateEvent } from '@philips-emr/tws-core/lib/component/types';
import { HttpService, ModalService } from '@philips-emr/tws-core/lib/services';

import { FragmentNames, SolicitarCancelamentoAliases } from '../../codegen';
import { Endpoints } from '../../definitions/endpoints';
import { DialogService, ToastService } from '../../services';
import BaseController from '../base/base-controller';

enum Step {
  INFORMACOES = 'informacoes',
  BENEFICIARIOS = 'beneficiarios',
  TITULAR = 'titular',
  FINANCEIRO = 'financeiro',
}

@Controller({ fragment: FragmentNames.SOLICITAR_CANCELAMENTO })
export default class SolicitarCancelamentoController extends BaseController {

  @Inject(SolicitarCancelamentoAliases.PANEL_LOADER)
  private panelLoader: PanelAPI;
  @Inject(SolicitarCancelamentoAliases.PANEL_WIZARD)
  private panelWizard: PanelAPI;
  @Inject(SolicitarCancelamentoAliases.PANEL_CARD)
  private panelCard: PanelAPI;
  @Inject(SolicitarCancelamentoAliases.PANEL_TITULAR)
  private panelTitular: PanelAPI;
  @Inject(SolicitarCancelamentoAliases.PANEL_FINANCEIRO)
  private panelFinanceiro: PanelAPI;
  @Inject(SolicitarCancelamentoAliases.WIZARD)
  private wizard: WizardAPI;
  @Inject(SolicitarCancelamentoAliases.FORM_INFORMACOES)
  private formInformacoes: FormAPI;
  @Inject(SolicitarCancelamentoAliases.FORM_BENEFICIARIOS)
  private formBeneficiarios: FormAPI;
  @Inject(SolicitarCancelamentoAliases.FORM_TITULAR)
  private formTitular: FormAPI;
  @Inject(SolicitarCancelamentoAliases.FORM_FINANCEIRO)
  private formFinanceiro: FormAPI;
  @Inject(SolicitarCancelamentoAliases.FIELDSET_BENEFICIARIOS_CHECKLIST)
  private fieldsetBeneficiariosChecklist: FieldsetAPI;
  @Inject(SolicitarCancelamentoAliases.FIELDSET_BENEFICIARIOS)
  private fieldsetBeneficiarios: FieldsetAPI;
  @Inject(SolicitarCancelamentoAliases.FIELDSET_TITULAR)
  private fieldsetTitular: FieldsetAPI;
  @Inject(SolicitarCancelamentoAliases.FIELDSET_FINANCEIRO)
  private fieldsetFinanceiro: FieldsetAPI;
  @Inject(SolicitarCancelamentoAliases.FIELD_NUMERO_CPF)
  private fieldNumeroCpf: FieldAPI;
  @Inject(SolicitarCancelamentoAliases.FIELD_TERMO)
  private fieldTermo: FieldAPI;
  @Inject(SolicitarCancelamentoAliases.INPUT_NUMERO_CPF)
  private inputNumeroCpf: InputAPI;
  @Inject(SolicitarCancelamentoAliases.CHECKLIST)
  private checklist: ChecklistAPI;
 
  private forms: { [key in Step]?: FormAPI } = {};
  private titular: string;
  private fieldsFinanceiro: FieldAPI[] = [];

  @OnMount(SolicitarCancelamentoAliases.PHONE_NUMBER_0)
  onMountPhone(event : MountEvent): void{
    const api = event.target as PhoneNumberAPI;
    api.setCountry(Country.BR);

  }
  
  @OnMount(SolicitarCancelamentoAliases.PHONE_NUMBER_1)
  onMountPhone1(event : MountEvent): void{
    const api = event.target as PhoneNumberAPI;
    api.setCountry(Country.BR);

  }

  @OnMount(SolicitarCancelamentoAliases.CONTEXTUAL_BAR)
  onMount(): void {
    const { nrSeqBeneficiario } = this.context.getBulkData();

    HttpService.get<[] | string>(Endpoints.GET.STEPS_CANCELAMENTO, { nrSeqBeneficiario })
      .then(({ body }) => {
        if (body) {

          if (typeof body === 'object') {
            this.panelWizard.setVisible(true);
            this.wizard.setHiddenSteps(body);
            return;
          }

          this.panelCard.setVisible(true);
        }
      })
      .catch(response => ToastService.open(response))
      .finally(() => catchRenderException(() => this.panelLoader.setVisible(false)));
  }

  @OnMount([
    SolicitarCancelamentoAliases.FORM_INFORMACOES,
    SolicitarCancelamentoAliases.FORM_BENEFICIARIOS,
    SolicitarCancelamentoAliases.FORM_TITULAR,
    SolicitarCancelamentoAliases.FORM_FINANCEIRO,
  ])
  onFormsdMount({ target }: MountEvent): void {
    const api = target as FormAPI;
    this.forms[api.getAlias().replace('form-', '') as Step] = api;
  }

  @OnMount(SolicitarCancelamentoAliases.LINK_TERMOS)
  onLinKTermoMount({ target }: MountEvent): void {
    const api = target as LinkAPI;
    const { nrSeqBeneficiario } = this.context.getBulkData();

    void HttpService.get<{ dsTermoCiencia: string }>(Endpoints.GET.TERMO_RESCISAO, { nrSeqBeneficiario })
      .then(({ body }) => {
        if (body) {
          const { dsTermoCiencia } = body;
          api.onClick.listen(() => ModalService.openDefault({
            closeable: true,
            theme: ThemeTonalRange.ULTRA_LIGHT,
            title: 'beneficiary.termos-rescisao',
            children: (<div style={{ wordWrap: 'break-word' }} dangerouslySetInnerHTML={{ __html: dsTermoCiencia }} />),
          }));
          return;
        }
        catchRenderException(() => this.fieldTermo.setVisible(false));
      })
      .catch(response => {
        ToastService.open(response);
        catchRenderException(() => this.fieldTermo.setVisible(false));
      });
  }

  @OnMount([
    SolicitarCancelamentoAliases.FIELD_FIN_PAGADOR,
    SolicitarCancelamentoAliases.FIELD_FIN_CPF,
    SolicitarCancelamentoAliases.FIELD_FIN_BANCO,
    SolicitarCancelamentoAliases.FIELD_FIN_AGENCIA,
    SolicitarCancelamentoAliases.FIELD_FIN_AGENCIA_DIGITO,
    SolicitarCancelamentoAliases.FIELD_FIN_CONTA,
    SolicitarCancelamentoAliases.FIELD_FIN_CONTA_DIGITO,
  ])
  onFieldsFinanceiroMount = ({ target }: MountEvent): number => this.fieldsFinanceiro.push(target as FieldAPI);

  @OnNavigate(SolicitarCancelamentoAliases.WIZARD)
  onNavigate({ from, to }: AbortableNavigateEvent<Step>): void {
    if (from === Step.BENEFICIARIOS && to === Step.TITULAR) {

      const { beneficiarios } = this.formBeneficiarios.getRecord() as { beneficiarios: [{ key: number }] };
      const nrSeqSeguradoSelecionados = beneficiarios.map(item => item.key).join('&nrSeqSeguradoSelecionados=');
      const selecionados = this.context.get('nrSeqSeguradoSelecionados');

      if (selecionados) {
        if (selecionados !== nrSeqSeguradoSelecionados) {
          catchRenderException(() => this.formTitular.setRecord({}));
        }
      }
      this.context.set({ nrSeqSeguradoSelecionados });
    }
  }

  @OnAfterNavigate(SolicitarCancelamentoAliases.WIZARD)
  onAfterNavigate({ from, to }: NavigateEvent<Step>): void {
    let form: FormAPI | undefined;

    form = this.forms[from];
    form && form.setState({ editing: false });

    if (!to) return;
    form = this.forms[to];
    form && form.setState({ editing: true });

    setTimeout(() => this.isStepsValid(to), 10);
  }

  @OnAfterFetch(SolicitarCancelamentoAliases.FETCH_BENEFICIARIOS_RESCISAO)
  onBeneficiariosRescisaoAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { beneficiarios, checklist, titular } = data as { titular: string, beneficiarios: ChecklistItem[], checklist: boolean };
      this.checklist.clear();
      this.checklist.setData(beneficiarios);

      if (beneficiarios.length === 1) {
        this.checklist.setShowCheckAll(false);
      }

      this.titular = titular;

      this.fieldsetBeneficiarios.setVisible(!checklist);
      this.fieldsetBeneficiariosChecklist.setVisible(checklist);
      
    }
  }

  @OnAfterFetch(SolicitarCancelamentoAliases.FETCH_FINANCEIRO)
  onFinanceiroAfterFetch({ data }: FetchEvent): void {
    if (data) {
      const { fieldsRequired } = data as { fieldsRequired: boolean };

      if (fieldsRequired) {
        this.fieldsFinanceiro.forEach((field) => field.setValidators([{ name: 'required' }]));
      }
    }
  }

  @OnValidate(SolicitarCancelamentoAliases.FORM_INFORMACOES)
  onFormInformacoesValidate({ target, valid }: ValidateEvent): void {
    if (this.wizard.getCurrentStepIdentifier() === Step.INFORMACOES) {
      const api = target as FormAPI;
      const { telefone, celular } = api.getRecord();
      this.wizard.setStepDone(Step.INFORMACOES, valid && !!(telefone || celular));
    }
  }

  @OnValidate(SolicitarCancelamentoAliases.FORM_BENEFICIARIOS)
  onFormBeneficiariosValidate(): void {
    if (this.wizard.getCurrentStepIdentifier() === Step.BENEFICIARIOS) {
      this.isBeneficiariosStepValid();
    }
  }

  @OnValidate(SolicitarCancelamentoAliases.FORM_TITULAR)
  onFormTitularValidate(): void {
    if (this.wizard.getCurrentStepIdentifier() === Step.TITULAR) {
      this.isTitularStepValid();
    }
  }

  @OnValidate(SolicitarCancelamentoAliases.FORM_FINANCEIRO)
  onFormFinanceiroValidate(): void {
    if (this.wizard.getCurrentStepIdentifier() === Step.FINANCEIRO) {
      this.isFinanceiroStepValid();
    }
  }

  @OnChange(SolicitarCancelamentoAliases.FORM_BENEFICIARIOS)
  onFormBeneficiariosChange({ value }: ChangeEvent<{ beneficiarios: [] }>): void {
    const { beneficiarios } = value;
    if (this.wizard.getCurrentStepIdentifier() === Step.BENEFICIARIOS) {
      this.wizard.setStepDone(Step.BENEFICIARIOS, !!beneficiarios.length);
    }
  }

  @OnChange(SolicitarCancelamentoAliases.CHECKLIST)
  onChecklistChange({ value, target }: ChangeEvent<[ChecklistItem]>): void {
    if (this.titular) {
      const api = target as unknown as ChecklistAPI;
      const data = api.getData();

      const containsTitular = value.find(item => item.key === this.titular);
      const containsUnchecked = data.length !== value.length;

      catchRenderException(() => {
        const check = !containsTitular || !containsUnchecked;
        this.panelTitular.setVisible(check);
        this.fieldsetTitular.setVisible(!check);
      });

      catchRenderException(() => {
        this.panelFinanceiro.setVisible(!containsTitular);
        this.fieldsetFinanceiro.setVisible(!!containsTitular);
      });

      if (value.find(item => item.key === this.titular)) {
        this.setDisabledKeys(data);
      } else {
        api.setDisabledKeys([]);
      }
    }
  }

  @OnChange(SolicitarCancelamentoAliases.FIELD_NOVO_TITULAR)
  onNovoTitularChange({ value }: ChangeEvent<{ nrCpf?: string }>): void {
    if (value) {
      const { nrCpf } = value;

      if (nrCpf) {
        this.fieldNumeroCpf.setValue(nrCpf);
        this.inputNumeroCpf.setReadOnly(true);
        return;
      }
    }

    this.fieldNumeroCpf.setValue('');
    this.inputNumeroCpf.setReadOnly(false);
  }

  @OnBeforeSubmit(SolicitarCancelamentoAliases.WIZARD)
  onBeforeSubmit({ abort }: AbortableSubmitEvent): void {
    const isSubmittable: Promise<boolean> = DialogService.confirm('beneficiary.aviso-cancelamento-submit')
      .then(() => this.submit())
      .catch(() => true);

    abort(isSubmittable);
  }

  private setDisabledKeys(beneficiarios: ChecklistItem[]): void {
    let list = beneficiarios;
    if (this.titular) {
      list = beneficiarios.filter(item => item.value && item.key !== this.titular);
    }

    const keys = list.map(item => item.key);
    this.checklist.setDisabledKeys(keys);
    keys.length && this.checklist.checkAll();
  }

  private isStepsValid(step: Step): void {
    switch (step) {
      case Step.BENEFICIARIOS:
        this.isBeneficiariosStepValid();
        break;
      case Step.TITULAR:
        this.isTitularStepValid();
        catchRenderException(() => this.checkCpfDisabled());
        break;
      case Step.FINANCEIRO:
        this.isFinanceiroStepValid();
        break;
    }
  }

  private isBeneficiariosStepValid(): void {
    const checklistValid = catchRenderException(() => this.checklist.getValue().length > 0, false);
    const listValid = catchRenderException(() => this.fieldsetBeneficiarios.isVisible(), false);
    this.wizard.setStepDone(Step.BENEFICIARIOS, listValid || checklistValid);
  }

  private isTitularStepValid(): void {
    const formValid = catchRenderException(() => this.formTitular.isValid(), false);
    const panelValid = catchRenderException(() => this.panelTitular.isVisible(), false);
    this.wizard.setStepDone(Step.TITULAR, formValid || panelValid);
  }

  private isFinanceiroStepValid(): void {
    const formValid = catchRenderException(() => this.formFinanceiro.isValid(), false);
    const panelValid = catchRenderException(() => this.panelFinanceiro.isVisible(), false);
    this.wizard.setStepDone(Step.FINANCEIRO, formValid || panelValid);
  }

  private checkCpfDisabled(): void {
    const record = this.formTitular.getRecord();
    const { nrCpfTitular, titular } = record as { nrCpfTitular: string, titular: { nrCpf: string } };
    if (nrCpfTitular && titular && titular.nrCpf && nrCpfTitular === titular.nrCpf) {
      this.inputNumeroCpf.setReadOnly(true);
    }
  }

  private setReadOnly(value: boolean): void {
    this.wizard.setReadOnly(value);
    if (value) {
      Object.values(this.forms).forEach(form => form && form.setState({ editing: false }));
    } else {
      const current = this.forms[this.wizard.getCurrentStepIdentifier() as Step];
      current && current.setState({ editing: true });
    }
  }

  private getRecord(): {} {
    const recordInformacoes = this.formInformacoes.getRecord();
    const { beneficiarios } = this.formBeneficiarios.getRecord() as { beneficiarios: [{ key: number, rest: {} }] };

    const isPanelTitular = catchRenderException(() => this.panelTitular.isVisible(), false);
    const isPanelFinanceiro = catchRenderException(() => this.panelFinanceiro.isVisible(), false);
    const recordTitular = catchRenderException(() => isPanelTitular ? {} : this.formTitular.getRecord(), {});
    const recordFinanceiro = catchRenderException(() => isPanelFinanceiro ? {} : this.formFinanceiro.getRecord(), {});

    const beneficiariosRescisao = beneficiarios ? beneficiarios.map(({ key: nrSeqSegurado, ...rest }) => ({ nrSeqSegurado, ...rest })) : [];

    return {
      ...recordInformacoes,
      beneficiariosRescisao,
      ...recordTitular,
      ...recordFinanceiro,
    };
  }

  private submit(): Promise<boolean> {
    this.setReadOnly(true);

    const { nrSeqBeneficiario } = this.context.getBulkData();
    const body = this.getRecord();

    return HttpService.post<{}>(Endpoints.POST.SOLICITAR_CANCELAMENTO, { nrSeqBeneficiario }, { body })
      .then(response => {
        response.body && ToastService.open(response);
        return false;
      })
      .catch(response => {
        ToastService.open(response);
        this.setReadOnly(false);
        return true;
      });
  }
}
